/**
 * Resolves the sending timezone of an outbound campaign from the leads it will
 * actually email.
 *
 * A Smartlead campaign has ONE sending window: `POST /campaigns/{id}/schedule`
 * takes a single `timezone` plus start/end hours. Smartlead does expose a
 * lead-level timezone, but only as a CSV custom variable (no API field), and
 * their own help centre currently advises against using it while they fix its
 * edge cases, so per-recipient scheduling is not an option today. The best we
 * can do is schedule for the audience the campaign is aimed at instead of
 * defaulting every campaign to US Eastern (ENG-1137).
 *
 * Everything here is deterministic matching on the free-text `location` we
 * already store per lead. No LLM: the launch path used one to guess a zone from
 * the study description, which is blind to the leads actually found, and the BYO
 * upload path had no signal at all.
 */

import { getCountryDisplayName } from "./countries";
import { matchLocationCountry, normalizeLocation } from "./location-country";

/**
 * Fallback when the leads carry no usable location. US Eastern matches both our
 * historical behaviour and Smartlead's guidance to point a campaign's default at
 * its largest audience segment.
 */
export const OUTBOUND_DEFAULT_TIMEZONE = "America/New_York";

/**
 * The window every outbound campaign sends in, expressed in the campaign's own
 * timezone. Shared so the Smartlead schedule payload and the copy we show the
 * recruiter can't drift apart.
 */
export const OUTBOUND_SENDING_WINDOW = {
  /** Smartlead weekday numbers, 0 = Sunday. Monday through Saturday. */
  days: [1, 2, 3, 4, 5, 6],
  startHour: "09:00",
  endHour: "21:00",
  /** How the window is phrased in the UI. */
  label: "9am-9pm, Monday to Saturday",
} as const;

/**
 * Share of the resolvable locations that must agree before we schedule for a
 * region. Below this the list is genuinely mixed (7 of 66 historical campaigns
 * are), and no single window is right, so the caller keeps its own choice.
 */
const MIN_DOMINANT_SHARE = 0.6;

/**
 * How many locations must resolve before we trust the majority. Small uploads
 * are exempt (a 2-lead list only ever has 2 votes), so this is a ceiling, not a
 * flat minimum.
 */
const MAX_REQUIRED_MATCHES = 3;

/**
 * The sending zone per country, keyed by ISO 3166-1 alpha-2. A campaign's
 * schedule is single-valued, so multi-zone countries get the zone covering the
 * largest business population. A country absent from this table resolves to no
 * opinion (the caller's own choice stands) rather than to a guessed zone.
 */
const REGION_TIMEZONES: Record<string, string> = {
  US: "America/New_York",
  CA: "America/Toronto",
  MX: "America/Mexico_City",
  BR: "America/Sao_Paulo",
  AR: "America/Argentina/Buenos_Aires",
  CL: "America/Santiago",
  CO: "America/Bogota",
  PE: "America/Lima",
  GB: "Europe/London",
  IE: "Europe/Dublin",
  PT: "Europe/Lisbon",
  ES: "Europe/Madrid",
  FR: "Europe/Paris",
  BE: "Europe/Brussels",
  NL: "Europe/Amsterdam",
  DE: "Europe/Berlin",
  CH: "Europe/Zurich",
  AT: "Europe/Vienna",
  IT: "Europe/Rome",
  SE: "Europe/Stockholm",
  NO: "Europe/Oslo",
  DK: "Europe/Copenhagen",
  FI: "Europe/Helsinki",
  PL: "Europe/Warsaw",
  CZ: "Europe/Prague",
  RO: "Europe/Bucharest",
  GR: "Europe/Athens",
  TR: "Europe/Istanbul",
  UA: "Europe/Kyiv",
  IL: "Asia/Jerusalem",
  AE: "Asia/Dubai",
  SA: "Asia/Riyadh",
  EG: "Africa/Cairo",
  NG: "Africa/Lagos",
  KE: "Africa/Nairobi",
  ZA: "Africa/Johannesburg",
  IN: "Asia/Kolkata",
  PK: "Asia/Karachi",
  BD: "Asia/Dhaka",
  SG: "Asia/Singapore",
  MY: "Asia/Kuala_Lumpur",
  ID: "Asia/Jakarta",
  TH: "Asia/Bangkok",
  VN: "Asia/Ho_Chi_Minh",
  PH: "Asia/Manila",
  HK: "Asia/Hong_Kong",
  CN: "Asia/Shanghai",
  JP: "Asia/Tokyo",
  KR: "Asia/Seoul",
  TW: "Asia/Taipei",
  AU: "Australia/Sydney",
  NZ: "Pacific/Auckland",
};

/**
 * Zones the sending-window pickers offer. Every zone the resolver can return,
 * plus the US zones it never picks on its own (it always answers Eastern for the
 * US) and UTC. Aiming a campaign at one US coast is a common manual override.
 * Callers merge in the current value, so a stored zone outside this list still
 * shows up.
 */
export const OUTBOUND_TIMEZONE_OPTIONS: readonly string[] = [
  ...new Set([
    ...Object.values(REGION_TIMEZONES),
    "America/Chicago",
    "America/Denver",
    "America/Los_Angeles",
    "UTC",
  ]),
].sort((a, b) => a.localeCompare(b));

export interface OutboundTimezoneResolution {
  /** IANA zone to schedule the campaign in. */
  timezone: string;
  /** Region the zone came from, for logs and the UI hint. */
  region: string;
  /** Fraction of the resolved locations that agreed on this region. */
  share: number;
  /** Locations we could resolve to a region. */
  matched: number;
  /** Non-empty locations we looked at. */
  total: number;
}

/**
 * Picks the sending timezone for a set of leads, or null when their locations are
 * missing, unrecognisable, or too mixed to justify one window. Null means "no
 * opinion," and the caller keeps whatever the recruiter chose (or the default).
 */
export function resolveOutboundTimezone(
  locations: readonly (string | null | undefined)[],
): OutboundTimezoneResolution | null {
  const strong = new Map<string, number>();
  const weak = new Map<string, number>();
  let total = 0;

  for (const raw of locations) {
    if (!raw?.trim()) {
      continue;
    }
    total += 1;
    const match = matchLocationCountry(raw);
    // A country without a zone here is no opinion, the same as no match.
    if (!match || !REGION_TIMEZONES[match.country]) {
      continue;
    }
    const counts = match.weak ? weak : strong;
    counts.set(match.country, (counts.get(match.country) ?? 0) + 1);
  }

  // Weak (two-letter code) matches only get a vote when nothing in the list
  // resolved the strong way.
  const counts = strong.size > 0 ? strong : weak;
  const matched = [...counts.values()].reduce((sum, n) => sum + n, 0);
  if (matched === 0 || matched < Math.min(MAX_REQUIRED_MATCHES, total)) {
    return null;
  }

  // Highest count wins; ties break on region code so the result is stable.
  const ranked = [...counts.entries()].sort(
    ([aRegion, aCount], [bRegion, bCount]) =>
      bCount - aCount || aRegion.localeCompare(bRegion),
  );
  const top = ranked[0];
  const timezone = top ? REGION_TIMEZONES[top[0]] : undefined;
  if (!top || !timezone) {
    return null;
  }

  const share = top[1] / matched;
  if (share < MIN_DOMINANT_SHARE) {
    return null;
  }

  return {
    timezone,
    region: getCountryDisplayName(top[0]),
    share,
    matched,
    total,
  };
}

/**
 * Merges a lead's separate country column into its location string, so a CSV that
 * splits "London" and "United Kingdom" across two columns still yields one
 * resolvable location (and one useful `location` in Smartlead). Idempotent: a
 * country already present in the location is not appended twice.
 */
export function composeLeadLocation(
  location?: string | null,
  country?: string | null,
): string | undefined {
  const trimmedLocation = location?.trim();
  const trimmedCountry = country?.trim();
  if (!trimmedCountry) {
    return trimmedLocation === "" ? undefined : trimmedLocation;
  }
  if (!trimmedLocation) {
    return trimmedCountry;
  }
  const parts = normalizeLocation(trimmedLocation)
    .split(",")
    .map((part) => part.trim());
  return parts.includes(normalizeLocation(trimmedCountry))
    ? trimmedLocation
    : `${trimmedLocation}, ${trimmedCountry}`;
}
